import { useEffect } from "react";
import { NavLink, useParams } from "react-router-dom";
import { CalendarDays, MapPin } from "lucide-react";
import { EmptyState, Spinner, StatusPill } from "@/components/common";
import { TripCtx, useLoad } from "@/context/TripContext";
import { dateRange } from "@/lib/api";
import Overview from "@/tabs/Overview";
import MyTrip from "@/tabs/MyTrip";
import Travelers from "@/tabs/Travelers";
import Itinerary from "@/tabs/Itinerary";
import Budget from "@/tabs/Budget";
import Rooms from "@/tabs/Rooms";
import Polls from "@/tabs/Polls";
import Lists from "@/tabs/Lists";
import Flights from "@/tabs/Flights";
import Docs from "@/tabs/Docs";
import News from "@/tabs/News";
import TripSettings from "@/tabs/TripSettings";
import Concierge from "@/tabs/Concierge";

const TABS = [
  ["overview", "Overview", Overview], ["me", "My trip", MyTrip], ["travelers", "Travelers", Travelers, true],
  ["itinerary", "Itinerary", Itinerary], ["budget", "Budget & payments", Budget, true], ["rooms", "Rooms", Rooms],
  ["polls", "Polls", Polls], ["lists", "Lists", Lists], ["flights", "Flights", Flights], ["docs", "Documents", Docs],
  ["news", "Announcements", News], ["concierge", "AI concierge", Concierge, true], ["settings", "Trip settings", TripSettings, true],
];

export default function TripWorkspace() {
  const { tripId, tab = "overview" } = useParams();
  const [d, reload, error] = useLoad(`/trips/${tripId}`);
  useEffect(() => {
    document.querySelector(`[data-testid="trip-tab-${tab}"]`)?.scrollIntoView({ inline: "nearest", block: "nearest" });
  }, [tab, d]);
  if (error) return <EmptyState title="Trip not found" text="This trip doesn't exist or you no longer have access." testid="trip-not-found" />;
  if (!d) return <Spinner />;
  const isOrg = d.is_organizer;
  const tabs = TABS.filter((t) => !t[3] || isOrg);
  const Active = (tabs.find((t) => t[0] === tab) || tabs[0])[2];
  const ctx = { trip: d.trip, me: d.me, isOrg, isOwner: d.is_owner, plan: d.plan, limits: d.limits, reload, tid: tripId, cur: d.trip.currency };
  return (
    <TripCtx.Provider value={ctx}>
      <div className="relative -mx-4 -mt-6 mb-5 h-44 overflow-hidden sm:mx-0 sm:mt-0 sm:h-52 sm:rounded-3xl">
        <img src={d.trip.cover_image || "https://images.unsplash.com/photo-1590767602124-234d1714ed92?crop=entropy&cs=srgb&fm=jpg&q=70&w=1200"} alt="" className="h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 via-slate-900/20 to-transparent" />
        <div className="absolute bottom-4 left-4 right-4 text-white sm:left-6">
          <div className="flex flex-wrap items-center gap-2"><StatusPill status={d.trip.status} testid="trip-status-pill" />
            <span className="pill bg-white/20 text-white backdrop-blur">{d.me.role.replace("_", "-")}</span>
            {d.plan !== "free" && <span className="pill bg-orange-500 text-white">{d.plan}</span>}</div>
          <h1 className="mt-2 text-2xl font-extrabold sm:text-4xl" data-testid="trip-title">{d.trip.name}</h1>
          <div className="mt-1 flex flex-wrap gap-4 text-sm text-white/90">
            {d.trip.destination && <span className="flex items-center gap-1"><MapPin className="h-4 w-4" />{d.trip.destination}</span>}
            <span className="flex items-center gap-1"><CalendarDays className="h-4 w-4" />{dateRange(d.trip.start_date, d.trip.end_date)}</span>
          </div>
        </div>
      </div>
      <nav className="no-scrollbar sticky top-16 z-30 -mx-4 mb-6 flex gap-2 overflow-x-auto scroll-px-4 bg-[#FAFAFA]/90 px-4 py-2 backdrop-blur sm:mx-0 sm:px-1 [mask-image:linear-gradient(to_right,transparent,black_12px,black_calc(100%-24px),transparent)]" data-testid="trip-tabs">
        {tabs.map(([k, label]) => (
          <NavLink key={k} to={`/trips/${tripId}/${k}`} className={() => `shrink-0 rounded-full px-4 py-2 text-sm font-semibold transition-colors ${tab === k || (k === "overview" && !tabs.some((t) => t[0] === tab)) ? "bg-orange-500 text-white" : "bg-white text-slate-600 ring-1 ring-slate-200 hover:bg-orange-50"}`} data-testid={`trip-tab-${k}`}>{label}</NavLink>
        ))}
      </nav>
      <div className="rise" key={tab}><Active /></div>
    </TripCtx.Provider>
  );
}
